/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { DrawerGroupList } from '@components/systems/drawerMenu/DrawerGroupList'
import { List } from '@mui/material'
import { mono, boFont, boAdmin } from '@components/systems/backoffice/backoffice.tokens'

import { useAppSelector, MenuType, UserMenuAccessType } from '@redux/index'
import { AccessControlManager } from '@guards'

/**
 * 사이드바 메뉴 트리. 항목 문법(전 깊이 공통, 클래스는 여기 한 곳에서 정한다):
 * 높이 36 · 13px · 비활성 dim → hover ink · 활성 ink 500 + 좌측 1px ink 룰.
 */
export const DrawerMenuList = () => {
	// redux state
	const mode: 'main' | 'admin' = useAppSelector(state => state.config.mode)
	const mainMenu: MenuType[] = useAppSelector(state => state.main.menu)
	const adminMenu: MenuType[] = useAppSelector(state => state.admin.menu)
	const userAccessMenuList: UserMenuAccessType[] = useAppSelector(
		state => state.auth.userAccessMenuList,
	)

	const menu = React.useMemo(
		() =>
			AccessControlManager.getMainMenuList({
				mode: mode,
				userAccessMenuList: userAccessMenuList,
				mainMenu: mainMenu,
				adminMenu: adminMenu,
			}),
		[mode, userAccessMenuList, mainMenu, adminMenu],
	)

	return (
		<List css={style} disablePadding className={mode === 'admin' ? 'admin' : ''}>
			{menu.map((aMenu: MenuType) => (
				<DrawerGroupList key={`first-${aMenu.id}`} menu={aMenu} />
			))}
		</List>
	)
}

const style = css({
	width: '100%',
	overflow: 'hidden',
	padding: '0 8px',
	fontFamily: boFont.ui,

	'.boMenuItem': {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		minHeight: 36,
		height: 36,
		padding: '0 10px',
		marginBottom: 2,
		borderRadius: 2,
		borderLeft: '1px solid transparent',
		fontFamily: boFont.ui,
		fontSize: 13,
		fontWeight: 400,
		color: mono.dim,
		transition:
			'color 200ms ease-out, background-color 200ms ease-out, border-color 200ms ease-out',

		'.boMenuIcon': {
			flexShrink: 0,
			fontSize: 18,
			color: 'inherit',
			lineHeight: 1,
		},
		/** Text 아톰의 래퍼 div — 남는 폭을 차지해야 말줄임이 걸린다 */
		'& > div': { flex: 1, minWidth: 0 },
		'.boMenuText': {
			display: 'block',
			fontFamily: 'inherit',
			flex: 1,
			minWidth: 0,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		'.boMenuCaret': {
			flexShrink: 0,
			fontSize: 16,
			color: mono.faint,
			transition: 'transform 200ms ease-out',
		},

		'&:hover': { color: mono.ink, background: 'rgba(22, 24, 27, 0.04)' },

		'&.active': {
			color: mono.ink,
			fontWeight: 500,
			borderLeftColor: mono.ink,
			borderRadius: 0,
		},
		'&.parentActive': { color: mono.ink },
		'&.expanded .boMenuCaret': { transform: 'rotate(90deg)' },
		'&.centered': { justifyContent: 'center', padding: 0 },
		'&.depth2': { paddingLeft: 38, height: 32, minHeight: 32, fontSize: 12.5 },
		'&.depth3': { paddingLeft: 56, height: 30, minHeight: 30, fontSize: 12.5 },
	},

	/* 관리자 모드 — 활성 룰만 아이스 블루 한 점(상단 바의 밑줄과 같은 점) */
	'&.admin .boMenuItem.active': { borderLeftColor: boAdmin.accent },
})
